import React, { createContext, useState, useContext, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useBranding } from './BrandingContext';
import { useLanguage } from './LanguageContext';

const ThemeContext = createContext(null);

const getDesignTokens = (mode, direction = 'rtl', brandColors = {}) => {
    const primaryColor = brandColors.primaryColor || '#1565C0';
    const secondaryColor = brandColors.secondaryColor || '#42A5F5';

    return {
        direction,
        palette: {
            mode,
            ...(mode === 'light'
                ? {
                    primary: { main: primaryColor },
                    secondary: { main: secondaryColor },
                    success: { main: '#22C55E' },
                    error: { main: '#EF4444' },
                    background: { default: '#f4f6f8', paper: '#ffffff' },
                }
                : {
                    primary: { main: primaryColor === '#1565C0' ? '#64B5F6' : primaryColor },
                    secondary: { main: secondaryColor },
                    success: { main: '#4CAF50' },
                    error: { main: '#EF5350' },
                    background: { default: '#121212', paper: '#1e1e1e' },
                    text: { primary: '#e0e0e0', secondary: '#aaaaaa' },
                }),
        },
        typography: { fontFamily: 'Inter, Rubik, sans-serif' },
        shape: { borderRadius: 12 },
        components: {
            MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
            MuiCssBaseline: {
                styleOverrides: {
                    html: { overflowX: 'hidden', overflowY: 'scroll', height: 'auto', minHeight: '100%' },
                    body: { transition: 'background-color 0.3s ease, color 0.3s ease', overflowX: 'hidden', overflowY: 'auto', height: 'auto', minHeight: '100%', WebkitOverflowScrolling: 'touch' },
                    '#root': { minHeight: '100vh', '@supports (height: 100svh)': { minHeight: '100svh' }, height: 'auto' },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
                        '& .MuiTableCell-head': { color: theme.palette.text.primary, fontWeight: 700, borderBottomColor: theme.palette.divider },
                    }),
                },
            },
            MuiTableCell: { styleOverrides: { root: ({ theme }) => ({ borderBottomColor: theme.palette.divider }) } },
            MuiTableRow: { styleOverrides: { root: ({ theme }) => ({ '&:hover': { backgroundColor: theme.palette.action.hover } }) } },
            MuiChip: { styleOverrides: { root: ({ theme }) => ({ ...(theme.palette.mode === 'dark' && { borderColor: theme.palette.divider }) }) } },
            MuiPaper: { styleOverrides: { root: () => ({ backgroundImage: 'none' }) } },
        },
    };
};

export const ThemeProviderWrapper = ({ children }) => {
    const { branding } = useBranding();
    const { isRtl } = useLanguage();
    const [mode, setMode] = useState(() => {
        try { return localStorage.getItem('emailLion_themeMode') || 'light'; } catch { return 'light'; }
    });

    const toggleTheme = () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
        localStorage.setItem('emailLion_themeMode', newMode);
    };

    const direction = isRtl ? 'rtl' : 'ltr';

    const brandColors = branding?.whitelabel ? {
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
    } : {};

    const theme = useMemo(
        () => createTheme(getDesignTokens(mode, direction, brandColors)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mode, direction, brandColors.primaryColor, brandColors.secondaryColor]
    );

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <MuiThemeProvider theme={theme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeMode = () => useContext(ThemeContext);
